// @ts-nocheck
import React, { useState } from 'react';

import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';

import { COMPANY_SURVEY_AGGREGATION, TRIGGER_SURVEY, MEETINGS_QUERY } from '../../../../graphql/meetings';

interface CallAssessmentSectionProps {
  companyId: number;
}

interface LikertScore {
  average: number;
  count: number;
  trend: number[];
}

interface SurveyResponse {
  id: number;
  meetingId: number;
  respondentId: number;
  answers: string;
  datetimeCreated: string;
}

const LIKERT_LABELS: Record<string, string> = {
  overall_impression: 'Overall Impression',
  management_quality: 'Management Quality',
  product_market_fit: 'Product-Market Fit',
  growth_potential: 'Growth Potential',
  culture_fit: 'Culture Fit',
};

const getLikertColor = (score: number) => {
  if (score <= 2) return '#d32f2f';
  if (score <= 3) return '#ed6c02';
  return '#2e7d32';
};

const CallAssessmentSection: React.FC<CallAssessmentSectionProps> = ({ companyId }) => {
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [triggerMeetingId, setTriggerMeetingId] = useState<number | null>(null);

  const { data, loading } = useQuery(COMPANY_SURVEY_AGGREGATION, {
    variables: { companyId },
    fetchPolicy: 'cache-and-network',
  });

  const { data: meetingsData } = useQuery(MEETINGS_QUERY, {
    variables: { companyId, limit: 50, offset: 0 },
    fetchPolicy: 'cache-and-network',
  });

  const [triggerSurvey, { loading: triggerLoading }] = useMutation(TRIGGER_SURVEY);

  const aggregation = data?.companySurveyAggregation;
  const totalResponses = aggregation?.totalResponses ?? 0;
  const likertScores: Record<string, LikertScore> = aggregation?.likertScores
    ? JSON.parse(aggregation.likertScores)
    : {};
  const multiSelectFrequencies: Record<string, Record<string, number>> = aggregation?.multiSelectFrequencies
    ? JSON.parse(aggregation.multiSelectFrequencies)
    : {};
  const responses: SurveyResponse[] = aggregation?.responses ? JSON.parse(aggregation.responses) : [];

  const meetings = meetingsData?.meetings ?? [];
  const pastMeetingsWithoutSurvey = meetings.filter((m: any) => {
    const isPast = new Date(m.meetingDate) < new Date();
    const hasSurvey = m.surveyResponses && m.surveyResponses.length > 0;
    return isPast && !hasSurvey && m.status !== 'cancelled';
  });

  const handleTriggerSurvey = async (meetingId: number) => {
    try {
      const result = await triggerSurvey({ variables: { meetingId } });
      const payload = result.data?.triggerSurvey;
      if (payload?.success) {
        setSnackbar({
          open: true,
          message: `${payload.notificationsSent} survey notification(s) sent`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: payload?.message || 'Failed to trigger survey',
          severity: 'error',
        });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Error triggering survey', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (totalResponses === 0) {
    return (
      <Box py={2}>
        <Typography variant="body2" color="text.secondary">
          No survey responses yet for this company.
        </Typography>
        {pastMeetingsWithoutSurvey.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Send survey for a past meeting:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {pastMeetingsWithoutSurvey.slice(0, 5).map((m: any) => (
                <Button
                  key={m.id}
                  size="small"
                  variant="outlined"
                  startIcon={<SendIcon />}
                  disabled={triggerLoading}
                  onClick={() => handleTriggerSurvey(m.id)}
                >
                  {format(new Date(m.meetingDate), 'MMM d, yyyy')}
                </Button>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    );
  }

  const sortedResponses = [...responses].sort(
    (a, b) => new Date(b.datetimeCreated).getTime() - new Date(a.datetimeCreated).getTime()
  );

  return (
    <Box>
      {/* Header with trigger button */}
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        {pastMeetingsWithoutSurvey.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<SendIcon />}
            disabled={triggerLoading}
            onClick={() => {
              const nextMeeting = pastMeetingsWithoutSurvey[0];
              if (nextMeeting) handleTriggerSurvey(nextMeeting.id);
            }}
          >
            Send Survey
          </Button>
        )}
      </Stack>

      {/* Aggregated Scores Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.entries(likertScores).map(([key, score]) => (
          <Grid item xs={12} md={6} key={key}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {LIKERT_LABELS[key] || key.replace(/_/g, ' ')}
                  </Typography>
                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography variant="h4" sx={{ color: getLikertColor(score.average), fontWeight: 600 }}>
                      {score.average.toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      / 5 ({score.count} responses)
                    </Typography>
                  </Stack>
                </Box>
                {score.trend && score.trend.length > 1 && (
                  <Box sx={{ mt: 1 }}>
                    <SparkLineChart
                      data={score.trend}
                      height={40}
                      width={120}
                      curve="natural"
                      area
                      colors={['#3F5D66']}
                    />
                  </Box>
                )}
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Multi-Select Tag Cloud */}
      {Object.keys(multiSelectFrequencies).length > 0 && (
        <Box sx={{ mb: 3 }}>
          {Object.entries(multiSelectFrequencies).map(([question, options]) => (
            <Box key={question} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {question.replace(/_/g, ' ')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Object.entries(options).map(([option, count]) => {
                  const percentage = Math.round((count / totalResponses) * 100);
                  const chipSize = percentage < 30 ? 'small' : 'medium';
                  const opacity = percentage < 30 ? 0.6 : percentage < 70 ? 0.8 : 1;
                  return (
                    <Chip
                      key={option}
                      label={`${option} (${percentage}%)`}
                      size={chipSize}
                      sx={{
                        bgcolor: `rgba(63, 93, 102, ${opacity})`,
                        color: '#fff',
                        mb: 0.5,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Box>
      )}

      {/* Response History */}
      {sortedResponses.length > 0 && (
        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Response History ({sortedResponses.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {sortedResponses.map(response => {
                const answers = typeof response.answers === 'string' ? JSON.parse(response.answers) : response.answers;
                return (
                  <Box
                    key={response.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Meeting #{response.meetingId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(response.datetimeCreated), 'MMM d, yyyy')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {answers &&
                        Object.entries(answers).map(([key, value]: [string, any]) => {
                          if (typeof value === 'number') {
                            return (
                              <Chip
                                key={key}
                                label={`${(LIKERT_LABELS[key] || key).substring(0, 15)}: ${value}`}
                                size="small"
                                sx={{
                                  bgcolor: getLikertColor(value),
                                  color: '#fff',
                                  fontSize: '0.7rem',
                                  mb: 0.5,
                                }}
                              />
                            );
                          }
                          if (Array.isArray(value)) {
                            return value.map((v: string) => (
                              <Chip
                                key={`${key}-${v}`}
                                label={v}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', mb: 0.5 }}
                              />
                            ));
                          }
                          return null;
                        })}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CallAssessmentSection;
